import { action } from '@ember/object';
import { next } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { PusherEvents } from '@databox/types/domains/ai/pusher.enums';
import type {
	AiPusherEvent,
	MetricVerificationUpdatedEvent,
	RunCompletedEvent,
	RunStartedEvent,
	ThreadCompletedEvent,
	ThreadUpdatedEvent,
} from '@databox/types/domains/ai/pusher.types';
import Cookies from 'js-cookie';
import pako from 'pako';
import type { Channel, Options as PusherOptions } from 'pusher-js';
import Pusher from 'pusher-js';

import type { APIData, EventData, RecordData, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { copyObject, reload } from 'webapp/utils/common';
import { getDatasetSyncUpdate, type RelayedSyncStatuses } from 'webapp/utils/dataset-sync-updates';
import { getChangedGoalIds } from 'webapp/utils/goal-updates';

import type { BlockRecord } from 'webapp/models';
import type { ConnectRequestData } from 'webapp/models-custom/connect-request';
import type { ConnectSelectionData } from 'webapp/models-custom/connect-selection';
import type InfolayerItemDatasetSync from 'webapp/models-custom/infolayer-item/dataset-sync';
import type { PromotionsData } from 'webapp/models-custom/promotions';

import type { BillingInfoData } from 'webapp/services/billing';
import { asyncReplyEventName } from 'webapp/services/event-manager';
import { Severity } from 'webapp/services/logger';
import { ForecastPusherEvents } from 'webapp/services/model-manager/forecasts';
import { SourcesPusherEvents } from 'webapp/services/model-manager/sources';
import { TeamsPusherEvents } from 'webapp/services/model-manager/teams';

const PARTIAL_EVENT_TIMEOUT = 30 * 1000;

type SocketEventData = APIData.Socket.ByEvent;

// events that BE can send compressed (`${name}C`)
type CompressedEventName =
	| 'bgEvent'
	| 'billingInfo'
	| 'billingPromotions'
	| 'connectRequests'
	| 'dataUpdated'
	| 'groupInfo'
	| 'logout'
	| 'resolutionCenter';

// and split into parts (`${name}CP`)
type PartialEventName = Extract<
	CompressedEventName,
	'bgEvent' | 'connectRequests' | 'dataUpdated' | 'resolutionCenter'
>;

type CompressedEventHandlers = {
	[E in CompressedEventName]: (data: SocketEventData[E], channelName?: string) => void;
};
type afterPusherAuthorizedResolveFn = (data?: unknown) => void;

// a connection 'error' event; pusher-js passes it untyped. Pusher's own errors carry data, socket errors carry error
interface PusherConnectionError {
	type?: string;
	data?: { code?: number; message?: string };
	error?: unknown;
}

// the options we build; pusher-js's ChannelAuthorizationOptions also wants a transport, which it defaults to 'ajax'
interface PusherAppOptions {
	cluster: string | null;
	channelAuthorization: {
		endpoint?: string;
		params?: { password?: string; isIframe?: boolean };
		headersProvider: () => Record<string, string>;
	};
}
type afterPusherAuthorizedRejectFn = () => void;
type afterDataChannelSubscribedResolveFn = (data?: unknown) => void;
type afterDataChannelSubscribedRejectFn = () => void;

// the received parts of split events, by event and uuid
type PartialDataForEvents = Record<PartialEventName, Record<string, string[]>>;

export default class SocketService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('logger') declare loggerService: Service.Logger;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('store') declare storeService: Service.Store;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('product-updates') declare productUpdatesService: Service.ProductUpdates;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;

	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('verification') declare verificationService: Service.Verification;

	private isPublicUrl = false;
	private dataPusher: Pusher | null = null;
	private partialData: PartialDataForEvents = {
		bgEvent: {},
		connectRequests: {},
		dataUpdated: {},
		resolutionCenter: {},
	};
	private compressedEventHandlers: CompressedEventHandlers = {
		bgEvent: (data, channelName) => this.handleBgEventEvent(data, channelName),
		billingInfo: (data) => this.handleBillingInfoEvent(data),
		billingPromotions: (data) => this.handleBillingPromotionsEvent(data),
		connectRequests: (data) => this.handleConnectRequestsEvent(data),
		dataUpdated: (data, channelName) => this.handleDataUpdatedEvent(data, channelName),
		groupInfo: (data) => this.handleGroupInfoEvent(data),
		logout: (data) => this.handleLogoutEvent(data),
		resolutionCenter: (data) => this.handleResolutionCenterEvent(data),
	};
	private _afterPusherAuthorizedResolveFn: afterPusherAuthorizedResolveFn | null = null;
	private _afterPusherAuthorizedRejectFn: afterPusherAuthorizedRejectFn | null = null;
	private _afterDataChannelSubscribedResolveFn: afterDataChannelSubscribedResolveFn | null = null;
	private _afterDataChannelSubscribedRejectFn: afterDataChannelSubscribedRejectFn | null = null;

	connectPusher: Pusher | null = null;
	lastConnectingTimestamp: number | null = null;
	afterPusherAuthorized: Promise<unknown> | null = null;
	afterDataChannelSubscribed: Promise<unknown> | null = null;
	datasetSyncInfoLayer: InfolayerItemDatasetSync | null = null;

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);

		this.afterPusherAuthorized = new Promise((resolve, reject) => {
			this._afterPusherAuthorizedResolveFn = resolve;
			this._afterPusherAuthorizedRejectFn = reject;
		});

		this.afterDataChannelSubscribed = new Promise((resolve, reject) => {
			this._afterDataChannelSubscribedResolveFn = resolve;
			this._afterDataChannelSubscribedRejectFn = reject;
		});
	}

	// -------- HANDLING EVENTS --------

	private forwardEvent<E extends CompressedEventName>(
		eventName: E,
		data: SocketEventData[E],
		channelName?: string,
	): void {
		this.compressedEventHandlers[eventName](data, channelName);
	}

	// bind with the event's payload type
	private bindEvent<E extends keyof SocketEventData>(
		channel: Channel,
		eventName: E,
		callback: (data: SocketEventData[E]) => void,
	): void {
		channel.bind(eventName, callback);
	}

	handleCompressedEvent<E extends CompressedEventName>(
		eventName: E,
		encodedData: string,
		channelName?: string,
	): void {
		let data: SocketEventData[E] | undefined;

		if (encodedData) {
			try {
				const charCodeArray = atob(encodedData)
					.split('')
					.map((x) => x.charCodeAt(0));
				data = JSON.parse(
					pako.inflate(new Uint8Array(charCodeArray), { to: 'string' }),
				) as SocketEventData[E];
			} catch (e) {
				const error = e as Error & { encodedData?: string };
				error.encodedData = encodedData;
				this.logError(`${eventName} C decode error`, error);
			}

			if (data) {
				this.forwardEvent(eventName, data, channelName);
			}
		}
	}

	handlePartialEvent(
		eventName: PartialEventName,
		data: APIData.Socket.EventPart,
		channelName?: string,
	): void {
		const uuid = data.uuid,
			partsReceived: string[] = [];
		let fullString = '';

		if (!this.partialData[eventName][uuid]) {
			this.partialData[eventName][uuid] = new Array<string>(data.totalParts);

			// if not all parts are received in 30s, log it (the event is never handled)
			window.setTimeout(() => {
				const parts = this.partialData[eventName][uuid];
				if (parts) {
					this.logError(
						'Partial event incomplete',
						{
							eventName,
							uuid,
							channelName,
							totalParts: data.totalParts,
							receivedParts: parts.filter(Boolean).length,
							lastConnectingTimestamp: this.lastConnectingTimestamp,
						},
						Severity.WARNING,
					);
				}
			}, PARTIAL_EVENT_TIMEOUT);
		}

		this.partialData[eventName][uuid][data.part - 1] = data.payload;

		this.partialData[eventName][uuid].forEach((part) => {
			if (part) {
				partsReceived.push(part);
			}
		});

		if (partsReceived.length === data.totalParts) {
			fullString = partsReceived.join('');
			this.handleCompressedEvent(eventName, fullString, channelName);
			delete this.partialData[eventName][uuid];
		}
	}

	private handlePusherConnectionError(
		data: PusherConnectionError['data'],
		postfix: string,
	): void {
		let errorCode: number | undefined = 0,
			msg;

		if (data?.code || data?.message) {
			errorCode = data.code;
			msg = 'Pusher error event';
		} else {
			// probably don't need to log this
			return;
			// msg = 'Pusher error empty?';
		}

		if (postfix) {
			msg += ` (${postfix})`;
		}

		if (
			errorCode !== undefined && // only a message: not logged
			errorCode !== 1006 &&
			(errorCode < 4100 || errorCode >= 4300)
		) {
			this.logError(msg, { data }, Severity.WARNING);
		}
	}

	private logError(msg: string, extradata?: object | null, severity?: Severity): void {
		this.loggerService.logError(msg, extradata, severity);
	}

	private logSocketEvent(msg: string, data?: object, channelName?: string): void {
		this.loggerService.logSocketEvent(msg, data, channelName);
	}

	// -------- dataUpdated -------
	private handleDataUpdatedEvent(data: APIData.App.DataUpdatedEvent, channelName?: string): void {
		if (data) {
			this.logSocketEvent('dataUpdated', data, channelName);

			delete data.origin;
			delete data.requestId;

			if (data.account) {
				// account data is separate
				this.accountService.update(data.account);
				delete data.account;
			}

			if (data.globalSettings) {
				this.accountService.updateGlobalSettings(data.globalSettings);
				delete data.globalSettings;
			}

			this.handleReturnedSamples(data.samples);

			this.checkCurrentUserVerified(data.users);

			this.filterGarbageAndPushToStore(data);

			this.validateEventInContextOfBoardData('dataUpdated', data, channelName);
		}
	}

	// ----------- reload -----------
	private handleReloadEvent(): void {
		this.logSocketEvent('reload');
		if (this.isPublicUrl) {
			reload();
		} else {
			this.navigationService.openReloadModal();
		}
	}

	// ----------- logout -----------
	private handleLogoutEvent(data: SocketEventData['logout']): void {
		let reallyLogout = true;

		this.logSocketEvent('logout', data);

		// if token is specified in data, we have to doublecheck if it's for the same token as we have
		if (data?.token) {
			if (typeof data.token === 'string') {
				data.token = [data.token];
			}
			reallyLogout = data.token.includes(this.currentUserService.userDataToken ?? '');
		}

		if (reallyLogout) {
			this.navigationService.logout('Socket event - reallyLogout');
		}
	}

	// -------- billingInfo --------
	private handleBillingInfoEvent(data: BillingInfoData): void {
		if (data) {
			this.logSocketEvent('billingInfo', data);
			this.billingService.resetRequestedInvoices();
			this.billingService.updateBillingInfo(data);
			this.billingService.clearSubscriptionPreviewCache();
			this.billingService.checkToAddBillingIssuePopup();
			this.billingService.invalidateFeatureCache?.();
		}
	}

	// -------- featureInfo --------
	private handleFeatureInfoEvent(data: SocketEventData['featureInfo']): void {
		if (data) {
			this.logSocketEvent('featureInfo', data);
			this.billingService.updateFeatureInfo(data);
		}
	}

	// -------- promotions --------
	private handleBillingPromotionsEvent(data: PromotionsData): void {
		if (data) {
			this.logSocketEvent('promotions', data);
			this.flowPromotionsService.updatePromotions(data);
		}
	}

	// ----- resolutionCenter -----
	private handleResolutionCenterEvent(
		data: APIData.AppNotifications.ResolutionCenterMessage[],
	): void {
		this.logSocketEvent('resolutionCenter', data);
		this.appNotificationsService.setBeNotifications(data);
	}

	// ----- connectRequests -----
	private handleConnectRequestsEvent(data: ConnectRequestData[]): void {
		this.logSocketEvent('connectRequests', data);
		this.appNotificationsService.updateConnectRequests(data);
	}

	// ----- connectSelections -----
	private handleConnectSelectionsEvent(data: ConnectSelectionData[]): void {
		this.logSocketEvent('connectSelections', data);
		this.appNotificationsService.updateConnectSelections(data);
	}

	// -------- bgEvent --------

	private handleBgEventEvent(data: APIData.App.BgEvent, channelName?: string): void {
		// delay for 3 runloops to avoid issues where bgEvent comes too soon after request
		// request itself has next, so we're being safe here.
		// async has promise wrapper also

		next(() => {
			next(() => {
				next(() => this.triggerBgEventDone(data, channelName));
			});
		});
	}

	private triggerBgEventDone(data: APIData.App.BgEvent, channelName?: string): void {
		this.logSocketEvent('bgEvent', data, channelName);

		const isSuccess = data && data.success !== false,
			requestId = data.requestId,
			origin = data.origin || '';
		// read before the store push, which strips deletedRecords
		const changedGoalIds = isSuccess ? getChangedGoalIds(data) : [];
		// not under the source-model update below: Genie shows datasets this tab never loaded
		const datasetSyncUpdate = isSuccess
			? getDatasetSyncUpdate(data, this.relayedDatasetSyncStatuses)
			: null;

		const isForecastPreviewEvent =
			origin === ForecastPusherEvents.FORECAST_PREVIEW_DATA_COMPLETE; // POST /preview
		const isForecastUpdateEvent = origin === ForecastPusherEvents.FORECAST_UPDATE_DATA_COMPLETE; // PUT /snapshot

		delete data.requestId;
		delete data.origin;
		delete data.success;

		if (data.account) {
			// account data is separate
			this.accountService.update(data.account);
			delete data.account;
		}

		this.checkCurrentUserVerified(data.users);

		if (
			isSuccess &&
			Object.keys(data).length > 0 &&
			// for asynch forecast events, we handle this separately in forecast event handler below
			!isForecastPreviewEvent &&
			!isForecastUpdateEvent
		) {
			// only the forecast origins excluded above set forecast to something else than a record
			this.filterGarbageAndPushToStore(data as RecordData.Payload);
		}

		this.handleReturnedSamples(data.samples);

		data.success = isSuccess;
		data.requestId = requestId; // Re-add requestId so bgEventHandlers can access it

		// normalize forecast events BEFORE triggering event handlers
		let normalizedEventData: APIData.Forecast.NormalizedForecastEventData | null = null;

		if (isForecastPreviewEvent || isForecastUpdateEvent) {
			// normalize both event types to a consistent structure
			normalizedEventData = this.mmForecastsService.normalizeForecastEventData(data, origin);

			// for update events, push the forecast model to the store
			if (isForecastUpdateEvent && data.forecasts?.[0]) {
				this.storeService.pushPayload({ forecasts: [data.forecasts[0]] });
			}

			// replace the original data with normalized structure for bgEventHandlers
			Object.assign(data, normalizedEventData);
		}

		// origin and account are removed, success is set (and the forecast data normalized) above
		const reply = data as EventData.AsyncReply;
		let wasTriggered = false,
			wasTriggeredOrigin = false;
		if (this.eventManagerService) {
			// without a requestId this triggers "undefined", like the origin trigger below
			wasTriggered = this.eventManagerService.trigger(
				asyncReplyEventName('', requestId),
				reply,
			);
			wasTriggeredOrigin = this.eventManagerService.trigger(
				asyncReplyEventName(origin, requestId),
				reply,
			);

			if (origin === SourcesPusherEvents.SOURCE_SYNC_STATUS_UPDATED) {
				// TODO: This is a temporary solution to update the source sync status.
				// We should refactor this so the analytics BE returns { sources: [] } array, then this would work out of the box.
				const spaceAccessId = data?.spaceAccessId;
				if (spaceAccessId) {
					const source = this.mmSourcesService.findModel(`${spaceAccessId}`);

					if (source) {
						source.directPropUpdate({
							// the sync status event always carries the type, status and start
							lastSync: {
								syncType: data.syncType!,
								syncStatus: data.syncStatus!,
								startedAt: data.startedAt!,
								finishedAt: data.finishedAt,
								lastSuccessfulSyncAt: data.lastSuccessfulSyncAt,
								dataRangeFrom: data.dataRangeFrom,
								dataRangeTo: data.dataRangeTo,
								dataAvailableAt: data.dataAvailableAt,
							},
						});

						this.datasetEventsService.handleDataSync(source, data);
					}
				}
			}

			if (
				origin === TeamsPusherEvents.UPDATED ||
				origin === TeamsPusherEvents.UPDATED_TEAM_MEMBERS
			) {
				this.mmTeamsService.onTeamUpdate(data.teams!); // team events always carry the teams
			}

			if (isForecastPreviewEvent || isForecastUpdateEvent) {
				wasTriggered = true;

				// Delegate all notification logic to the model manager
				if (normalizedEventData) {
					this.mmForecastsService.handleForecastNotificationDisplay(normalizedEventData);
				}
			}
		}

		if (!wasTriggered && !wasTriggeredOrigin) {
			if (!isSuccess && data?.message) {
				this.infolayerService.error(data.message);
			}
		}

		this.validateEventInContextOfBoardData('bgEvent', data, channelName);

		// last, so a throwing React listener cannot skip the store push or the bgEvent handlers
		changedGoalIds.forEach((goalId) => {
			this.notifyAiEventListeners({ type: PusherEvents.GOAL_UPDATED, data: { goalId } });
		});
		if (datasetSyncUpdate) {
			this.notifyAiEventListeners({
				type: PusherEvents.DATASET_SYNC_UPDATED,
				data: datasetSyncUpdate,
			});
		}
	}

	private filterGarbageAndPushToStore(data: RecordData.Payload) {
		if (!this.isPublicUrl) {
			const garbageCollectorService = this.garbageCollectorService;
			if (garbageCollectorService) {
				data = garbageCollectorService.filterSocketDataUpdates(data);
			}
		}

		if (Object.keys(data).length) {
			this.storeService.pushPayload(data);
		}
	}

	// must run before the users are pushed to the store (isVerified reads the user record)
	private checkCurrentUserVerified(users?: RecordData.User.Raw[]): void {
		const updatedUser = users?.length === 1 ? users[0] : undefined;
		if (
			updatedUser &&
			this.currentUserService.id === String(updatedUser.id) &&
			this.currentUserService.isVerified !== updatedUser.isVerified
		) {
			// we trigger userVerified to re-render connect wrapper in container.ts
			this.currentUserService.isVerified = updatedUser.isVerified;

			this.eventManagerService.trigger(AppEvent.USER_VERIFIED);
		}
	}

	private handleReturnedSamples(samples?: RecordData.Sample.Raw[]): void {
		if (!samples) {
			return;
		}

		samples.forEach((sample) => {
			this.mmBlocksService.removeBlockIdFromRequestedSamples(String(sample.id));
		});
	}

	// -------- globalVisualSettings --------
	private handleGlobalVisualSettingsEvent(data: APIData.GlobalVisualSettings.Response): void {
		this.logSocketEvent('globalSettings', data);
		this.accountService.updateGlobalSettings(data);
	}

	// -------- systemStatus --------
	private handleSystemStatusEvent(data: SocketEventData['systemStatus']): void {
		if (data) {
			this.logSocketEvent('systemStatus', data);
			this.appNotificationsService.systemStatus = data.status;
		}
	}

	// -------- productUpdate --------
	private handleProductUpdateEvent(data: SocketEventData['productUpdate']): void {
		// handles publis/unpublish of product update messages from Platform Admin
		if (data) {
			this.logSocketEvent('productUpdate', data);

			if (data.publish) {
				const updatedMessage = data.activityMessages[0]!;
				this.productUpdatesService.lastProductUpdate = updatedMessage.created;
				this.productUpdatesService.updateProductUpdate(updatedMessage);
			} else {
				const updatedMessage = data.activityMessages[0]!;
				this.productUpdatesService.lastProductUpdate =
					this.userSettingsService.lastSeenProductUpdates;
				this.productUpdatesService.removeProductUpdate(updatedMessage.id);
			}
		}
	}

	// -------- summaryLimitsUpdated --------
	private handleSummaryLimitsUpdatedEvent(
		data: APIData.GenerativeAI.DataPerformance.SummaryLimitsResponse,
	): void {
		if (data) {
			this.logSocketEvent('summaryLimitsUpdated', data);
			this.mmDataPerformanceSummariesService.updateLimits(data?.limits || []);
		}
	}

	// -------- groupInfo --------
	private handleGroupInfoEvent(data: SocketEventData['groupInfo']): void {
		this.logSocketEvent('groupInfo', data);
		if ('deletedRecords' in data) {
			this.storeService.pushPayload(data);
		} else {
			this.storeService.pushPayload({ group: data });
		}
	}

	// -------- HANDLING PUSHER CONNECTION --------
	get privateChannelPrefixWithEnvId(): string {
		let prefix = 'private';
		const envId = this.requestService.pusherEnvId;
		if (envId) {
			prefix += `-${envId}`;
		}
		return prefix;
	}

	private initPusher(options: PusherOptions, key = this.requestService.pusherKey): Pusher | null {
		return new Pusher(key!, options);
	}

	private getChannelName(): string {
		let channelName;

		const prefix = this.privateChannelPrefixWithEnvId;

		if (this.isPublicUrl) {
			channelName = `${prefix}-wall-${this.requestService.wallToken}`;
		} else {
			channelName = `${prefix}-user-${this.accountService.id}-${this.currentUserService.id || ''}`;
		}

		return channelName;
	}

	updatePusherAuthToken(): void {
		// when coming out of sleep / no wifi, pusher will try to reconnect the channels
		// if reconnect has been tried (and failed) before the Auth header was updated, we need to manually trigger reconnect again
		const pusherInstances = [this.dataPusher, this.authPusher];
		pusherInstances.forEach((pusher) => {
			if (pusher) {
				const channels = pusher.channels?.channels || {};
				Object.entries(channels).forEach(([channelName, channel]) => {
					if (!channel.subscribed) {
						pusher.subscribe(channelName);
					}
				});
			}
		});
	}

	getNewConnectChannel(channelName: string): Channel | null {
		const connectApiUrl = this.requestService.connectApiUrl;
		let pusher = this.connectPusher,
			channel = null;

		if (!pusher) {
			pusher = this.initPusher({
				authEndpoint: `${connectApiUrl}/pusher/auth`,
				cluster: this.requestService.pusherCluster!,
			});
			this.connectPusher = pusher;
		}

		if (pusher) {
			pusher.connection.bind('error', (err: PusherConnectionError) => {
				this.handlePusherConnectionError(err.data, 'connect');
			});

			if (!pusher.channel(channelName)) {
				channel = pusher.subscribe(channelName);
			}
		}

		return channel;
	}

	subscribeToAppEvents(
		isPublicUrl?: boolean,
		opts: { password?: string | null; isIframe?: boolean } = {},
	): void {
		if (this.dataPusher) {
			return;
		}

		const requestService = this.requestService;
		const options: PusherAppOptions = {
			channelAuthorization: {
				headersProvider: () => {
					return {
						Authorization: this.currentUserService.authTokenAuthorizationHeader,
						'X-Authorization': this.currentUserService.userTokenAuthorizationHeader,
					};
				},
			},
			cluster: this.requestService.pusherCluster,
		};

		this.isPublicUrl = !!isPublicUrl;

		if (isPublicUrl === true) {
			options.channelAuthorization.endpoint = `${requestService.apiUrl}/auth/pusher-token/${requestService.wallToken}`;

			const authParams: NonNullable<PusherAppOptions['channelAuthorization']['params']> = {};
			if (opts.password) {
				authParams.password = opts.password;
			}

			if (opts.isIframe) {
				authParams.isIframe = opts.isIframe;
			}

			if (Object.keys(authParams).length) {
				options.channelAuthorization.params = authParams;
			}
		} else {
			options.channelAuthorization.endpoint = `${requestService.apiUrl}/auth/pusher`;
		}

		const pusher = this.initPusher(options as PusherOptions);
		if (!pusher) {
			return;
		}

		pusher.connection.bind('error', (err: PusherConnectionError) => {
			if (this._afterPusherAuthorizedRejectFn) {
				this._afterPusherAuthorizedRejectFn();
			}

			this.handlePusherConnectionError(err.data, '');
		});

		pusher.connection.bind('connected', () => {
			if (this._afterPusherAuthorizedResolveFn) {
				this._afterPusherAuthorizedResolveFn();
			}
		});

		// if (opts.onReconnect) {
		// 	later(() => {
		// 		pusher.connection.bind('connected', opts.onReconnect);
		// 	}, 5000);
		// }

		const channelName = this.getChannelName();
		if (!pusher.channel(channelName)) {
			const channel = pusher.subscribe(channelName);

			channel.bind('pusher:subscription_succeeded', () => {
				if (this._afterDataChannelSubscribedResolveFn) {
					this._afterDataChannelSubscribedResolveFn();
				}
			});

			channel.bind('pusher:subscription_error', () => {
				if (this._afterDataChannelSubscribedRejectFn) {
					this._afterDataChannelSubscribedRejectFn();
				}
			});

			this.subscribeChannelToDataEvents(channel);

			this.bindEvent(channel, 'globalSettings', (data) => {
				this.handleGlobalVisualSettingsEvent(data);
			});

			this.bindEvent(channel, 'featureInfo', (data) => {
				this.handleFeatureInfoEvent(data);
			});

			this.bindEvent(channel, 'billingInfo', (data) => {
				this.handleBillingInfoEvent(data);
			});

			channel.bind('billingInfoC', (data: string) => {
				this.handleCompressedEvent('billingInfo', data);
			});

			this.bindEvent(channel, 'billingPromotions', (data) => {
				this.handleBillingPromotionsEvent(data);
			});

			channel.bind('billingPromotionsC', (data: string) => {
				this.handleCompressedEvent('billingPromotions', data);
			});

			channel.bind('reload', () => {
				this.handleReloadEvent();
			});

			this.bindEvent(channel, 'logout', (data) => {
				this.handleLogoutEvent(data);
			});

			channel.bind('logoutC', (data: string) => {
				this.handleCompressedEvent('logout', data);
			});

			this.bindEvent(channel, 'systemStatus', (data) => {
				this.handleSystemStatusEvent(data);
			});

			this.bindEvent(channel, 'summaryLimitsUpdated', (data) => {
				this.handleSummaryLimitsUpdatedEvent(data);
			});

			this.bindEvent(channel, 'groupInfo', (data) => {
				this.handleGroupInfoEvent(data);
			});

			channel.bind('groupInfoC', (data: string) => {
				this.handleCompressedEvent('groupInfo', data);
			});

			if (isPublicUrl !== true) {
				this.bindEvent(channel, 'resolutionCenter', (data) => {
					this.handleResolutionCenterEvent(data);
				});
				channel.bind('resolutionCenterC', (data: string) => {
					this.handleCompressedEvent('resolutionCenter', data);
				});
				channel.bind('resolutionCenterCP', (data: APIData.Socket.EventPart) => {
					this.handlePartialEvent('resolutionCenter', data);
				});

				this.bindEvent(channel, 'connectRequests', (data) => {
					this.handleConnectRequestsEvent(data);
				});
				channel.bind('connectRequestsC', (data: string) => {
					this.handleCompressedEvent('connectRequests', data);
				});
				channel.bind('connectRequestsCP', (data: APIData.Socket.EventPart) => {
					this.handlePartialEvent('connectRequests', data);
				});

				this.bindEvent(channel, 'connectSelections', (data) => {
					this.handleConnectSelectionsEvent(data);
				});

				this.bindEvent(channel, 'productUpdate', (data) => {
					this.handleProductUpdateEvent(data);
				});

				let lastActivity: number; // log "last activity" while pusher is connected
				setInterval(
					() => {
						try {
							if (pusher?.connection?.state === 'connected') {
								lastActivity = Date.now();
							}
						} catch (_e) {
							// skip
						}
					},
					5 * 60 * 1000,
				);

				pusher.connection.bind(
					'state_change',
					(states: { previous: string; current: string }) => {
						// states = {previous: 'oldState', current: 'newState'}
						if (states.current === 'connecting') {
							this.lastConnectingTimestamp = Date.now();
						} else if (states.current === 'connected') {
							if (lastActivity) {
								if (Date.now() - lastActivity > 4 * 60 * 60 * 1000) {
									this.navigationService.pusherLongtermInactivity();
								}
							} else {
								lastActivity = Date.now();
							}
						}
					},
				);
			}
		}

		this.dataPusher = pusher;
	}

	unsubscribeFromChannel(channelName: string): void {
		const pusher = this.dataPusher,
			unsubFunc = function () {
				if (pusher?.channel(channelName)?.subscribed) {
					pusher.unsubscribe(channelName);
				}
			};

		if (pusher?.channel(channelName)) {
			if (pusher.channel(channelName).subscribed) {
				unsubFunc();
			} else {
				pusher.channel(channelName).bind('pusher:subscription_succeeded', unsubFunc);
			}
		}
	}

	private checkForActivePusherConnection(pusher: Pusher | null): boolean {
		if (!pusher) {
			this.logError('pusher not loaded for channel subscribe', null, Severity.CRITICAL);
			return false;
		}

		return true;
	}

	private subscribeChannelToDataEvents(channel: Channel): void {
		this.bindEvent(channel, 'dataUpdated', (data) => {
			this.handleDataUpdatedEvent(data, channel.name);
		});
		channel.bind('dataUpdatedC', (data: string) => {
			this.handleCompressedEvent('dataUpdated', data, channel.name);
		});
		channel.bind('dataUpdatedCP', (data: APIData.Socket.EventPart) => {
			this.handlePartialEvent('dataUpdated', data, channel.name);
		});

		this.bindEvent(channel, 'bgEvent', (data) => {
			this.handleBgEventEvent(data, channel.name);
		});
		channel.bind('bgEventC', (data: string) => {
			this.handleCompressedEvent('bgEvent', data, channel.name);
		});
		channel.bind('bgEventCP', (data: APIData.Socket.EventPart) => {
			this.handlePartialEvent('bgEvent', data, channel.name);
		});

		this.bindEvent(channel, 'verificationChanged', (data) => {
			this.verificationService.handlePusherEvent(data);
		});
	}

	// -------- BOARD DATA --------

	@tracked private subscribedToBoardDataIds: number[] = [];
	@tracked private subscribingToBoardDataIds: Record<
		number,
		Promise<Channel | null> | undefined
	> = {};

	async subscribeToBoardData(boardId: number): Promise<Channel | null> {
		const pusher = this.dataPusher;

		if (!this.checkForActivePusherConnection(pusher)) {
			return Promise.resolve(null);
		}

		const subscribingPromise = this.subscribingToBoardDataIds[boardId];
		if (subscribingPromise) {
			return subscribingPromise;
		}

		const channelName = this.getBoardDataChannelName(boardId);

		let channel = pusher!.channel(channelName);

		if (!channel) {
			channel = pusher!.subscribe(channelName);

			// BE sends only dataUpdated events here, not bgEvents,
			// but we subscribe anyway, does not hurt
			this.subscribeChannelToDataEvents(channel);

			const subscribePromise = new Promise<Channel | null>((resolve) => {
				channel.bind('pusher:subscription_succeeded', () => {
					this.removeFromSubscribingToBoardDataIds(boardId);
					this.addToSubscribedToBoardDataIds(boardId);
					resolve(channel);
				});
				channel.bind('pusher:subscription_error', () => {
					this.removeFromSubscribingToBoardDataIds(boardId);
					resolve(null);
				});
			});

			this.addToSubscribingToBoardDataIds(boardId, subscribePromise);

			return subscribePromise;
		}

		return Promise.resolve(channel);
	}

	unsubscribeFromBoardData(boardId: number): void {
		const channelName = this.getBoardDataChannelName(boardId);

		this.removeFromSubscribedToBoardDataIds(boardId);

		this.unsubscribeFromChannel(channelName);
	}

	private getBoardDataChannelName(boardId: number): string {
		let channelName = this.getChannelName();
		channelName += `-board-${boardId}`;
		return channelName;
	}

	private addToSubscribingToBoardDataIds(
		boardId: number,
		promise: Promise<Channel | null>,
	): void {
		this.subscribingToBoardDataIds[boardId] = promise;
		this.subscribingToBoardDataIds = this.subscribingToBoardDataIds;
	}

	private removeFromSubscribingToBoardDataIds(boardId: number): void {
		this.subscribingToBoardDataIds[boardId] = undefined;
		this.subscribingToBoardDataIds = this.subscribingToBoardDataIds;
	}

	private addToSubscribedToBoardDataIds(boardId: number): void {
		if (this.subscribedToBoardDataIds.includes(boardId)) {
			return;
		}
		this.subscribedToBoardDataIds.push(boardId);
		this.loggerService.logToConsole(
			`subscribing to ${boardId}, now subscribed to:`,
			this.subscribedToBoardDataIds,
		);
	}

	private removeFromSubscribedToBoardDataIds(boardId: number): void {
		if (!this.subscribedToBoardDataIds.includes(boardId)) {
			return;
		}
		this.subscribedToBoardDataIds = this.subscribedToBoardDataIds.filter(
			(id) => id !== boardId,
		);
		this.loggerService.logToConsole(
			`unsubscribing from ${boardId}, now subscribed to:`,
			this.subscribedToBoardDataIds,
		);
	}

	private validateEventInContextOfBoardData(
		eventName: 'dataUpdated' | 'bgEvent',
		data: SocketEventData['dataUpdated'] | SocketEventData['bgEvent'],
		channelName?: string,
	): void {
		if (!channelName) {
			return;
		}

		if (channelName.includes('-board-')) {
			// board data channels currently support only dataUpdated / bgEvent with just sample data
			if (['dataUpdated', 'bgEvent'].includes(eventName)) {
				const dataCopy = copyObject(data) as Record<string, unknown>;
				const allowedKeys = ['origin', 'requestId', 'success', 'samples'];
				Object.keys(dataCopy).forEach((key) => {
					if (allowedKeys.includes(key)) {
						delete dataCopy[key];
					}
				});
				if (Object.keys(dataCopy).length) {
					this.loggerService.logError(
						'[Pusher] Unexpected entities on board data channel',
						Object.assign({ channelName }, dataCopy),
						Severity.WARNING,
					);
				}
			} else {
				this.loggerService.logError(
					'[Pusher] Unexpected event on board data channel',
					Object.assign({ channelName, eventName }, data),
					Severity.WARNING,
				);
				return;
			}
		} else if (!channelName.includes('-wall-')) {
			// samples for blocks that have boardId should not be sent over this channel
			const sampleIdsReceived = (data.samples || []).map((sample) => sample.id);
			sampleIdsReceived.forEach((blockId) => {
				const block = this.storeService.peekRecord('block', blockId) as BlockRecord;
				if (block?.boardId) {
					this.loggerService.logError(
						'[Pusher] Sample from board received on general channel',
						Object.assign({ channelName, blockId }, data),
						Severity.WARNING,
					);
				}
			});
		}
	}

	// -------- BOARD PREVIEWS --------

	subscribeToBoardPreview(previewHash: string): Channel | null {
		const pusher = this.dataPusher;

		if (!this.checkForActivePusherConnection(pusher)) {
			return null;
		}

		let channelName = this.getChannelName();
		channelName += `-preview-${previewHash}`;

		let channel = pusher!.channel(channelName);

		if (!channel) {
			channel = pusher!.subscribe(channelName);

			this.subscribeChannelToDataEvents(channel);
		}

		return channel;
	}

	unsubscribeFromBoardPreview(previewHash: string): void {
		let channelName = this.getChannelName();
		channelName += `-preview-${previewHash}`;

		this.unsubscribeFromChannel(channelName);
	}

	// ----- Auth pusher -----

	private authPusher: Pusher | null = null;
	authPusherKey = '';
	authPusherCluster = '';

	subscribeToAuthEvents() {
		if (this.authPusher) {
			return;
		}

		if (!this.navigationService.isMainApp) {
			return;
		}

		const guid = this.currentUserService.userGuid;

		if (!guid || !this.authPusherKey || !this.authPusherCluster) {
			return;
		}

		const options: PusherOptions = {
			channelAuthorization: {
				transport: 'ajax',
				endpoint: `${this.requestService.apiUrl}/auth/auth-pusher`,
				headersProvider: () => {
					return {
						Authorization: this.currentUserService.authTokenAuthorizationHeader,
						'X-Authorization': this.currentUserService.userTokenAuthorizationHeader,
					};
				},
			},
			cluster: this.authPusherCluster,
		};

		const pusher = this.initPusher(options, this.authPusherKey);
		if (!pusher) {
			return;
		}

		pusher.connection.bind('error', (err: PusherConnectionError) => {
			this.handlePusherConnectionError(err.data, 'auth');
		});

		const channelName = `${this.privateChannelPrefixWithEnvId}-auth-${guid}`;
		if (!pusher.channel(channelName)) {
			const channel = pusher.subscribe(channelName);
			channel.bind('user_logout', (data: APIData.Socket.UserLogoutEvent) => {
				this.handleUserLogoutEvent(data);
			});
		}

		this.authPusher = pusher;
	}

	handleUserLogoutEvent(data: APIData.Socket.UserLogoutEvent) {
		if (data) {
			this.logSocketEvent('user_logout', data);
			if (data.rt) {
				void this.requestService
					.app<APIData.Auth.LogoutCheckResponse>('POST', '/auth/logout-check', {
						rt: data.rt,
					})
					.then((response) => {
						if (response?.mustLogout) {
							this.navigationService.openLogoutPopup();
						}
					})
					.catch();
			} else if (data.sid) {
				const sidCookie = Cookies.get(this.accountService.cookieAuthSID.name);
				if (!sidCookie || data.sid === sidCookie) {
					this.navigationService.openLogoutPopup();
				}
			}
		}
	}

	// ----- AI pusher -----
	//
	// Long-lived, space+user scoped (not thread-scoped) - reaches the FE regardless of which
	// Genie page is open. Relays thread.completed/run.completed/run.started/thread.updated, via
	// ai-bff's PRIVATE_AI_BFF channel. Listeners also get three events ai-bff does not relay:
	// goal.updated and dataset.sync_updated, which triggerBgEventDone builds from data-channel
	// bgEvents (goal changes, source sync status updates), and metric-verification.updated, which
	// the verification service announces.

	private aiPusher: Pusher | null = null;
	private aiEventListeners = new Set<(event: AiPusherEvent) => void>();
	// Per tab, so each tab's dataset cards refetch once per status change, not per progress tick.
	private relayedDatasetSyncStatuses: RelayedSyncStatuses = new Map();

	/** Eagerly establishes the connection at app boot - called once from the data loader. */
	subscribeToAiConnection(): void {
		if (this.aiPusher) {
			return;
		}

		const spaceId = this.accountService.id;
		const userId = this.currentUserService.id;

		if (!spaceId || !userId) {
			return;
		}

		const options: PusherOptions = {
			channelAuthorization: {
				transport: 'ajax',
				endpoint: `${this.requestService.aiBffUrl}/api/v1/pusher/auth`,
				// ai-bff's AuthMiddleware requires x-space-id/x-user-id alongside the auth tokens
				// (requireHeader in auth-middleware.ts); without them the 401 is silent - pusher-js
				// never sends the websocket subscribe frame. The per-thread flow sends the same four
				// (ThreadConnectionProvider.helpers.ts in react-monorepo).
				headersProvider: () => {
					return {
						Authorization: this.currentUserService.authTokenAuthorizationHeader,
						'X-Authorization': this.currentUserService.userTokenAuthorizationHeader,
						'X-Space-Id': `${spaceId}`,
						'X-User-Id': `${userId}`,
					};
				},
			},
			cluster: this.requestService.pusherCluster!,
		};

		const pusher = this.initPusher(options);
		if (!pusher) {
			return;
		}

		pusher.connection.bind('error', (err: PusherConnectionError) => {
			this.handlePusherConnectionError(err.data, 'ai');
		});

		// PRIVATE_AI_BFF puts the env id after the full `private-ai-bff` prefix (getPusherChannelName
		// in @databox/shared), not right after `private` like this service's other channel families -
		// `privateChannelPrefixWithEnvId` would build a name ai-bff's /pusher/auth rejects.
		const envId = this.requestService.pusherEnvId;
		const channelName = envId
			? `private-ai-bff-${envId}-${spaceId}-${userId}`
			: `private-ai-bff-${spaceId}-${userId}`;
		if (!pusher.channel(channelName)) {
			const channel = pusher.subscribe(channelName);

			channel.bind(PusherEvents.THREAD_COMPLETED, (data: ThreadCompletedEvent) => {
				this.notifyAiEventListeners({ type: PusherEvents.THREAD_COMPLETED, data });
			});

			channel.bind(PusherEvents.RUN_COMPLETED, (data: RunCompletedEvent) => {
				this.notifyAiEventListeners({ type: PusherEvents.RUN_COMPLETED, data });
			});

			// A run this tab did not start - another tab or device, or a routine run - is joined only
			// through this event; without it the chat shows nothing until the run ends.
			channel.bind(PusherEvents.RUN_STARTED, (data: RunStartedEvent) => {
				this.notifyAiEventListeners({ type: PusherEvents.RUN_STARTED, data });
			});

			// A new thread's title lands after its turn ended, so it cannot ride on the run's stream.
			channel.bind(PusherEvents.THREAD_UPDATED, (data: ThreadUpdatedEvent) => {
				this.notifyAiEventListeners({ type: PusherEvents.THREAD_UPDATED, data });
			});
		}

		this.aiPusher = pusher;
	}

	/**
	 * Registers a listener for the relay's events, plus the bgEvent-built ones and
	 * metric-verification.updated from the verification service, and returns an unsubscribe function.
	 * Handed to React as the `subscribeToAiEvents` host prop (see BaseAiProps in ember-integrations).
	 */
	@action
	subscribeToAiEvents(listener: (event: AiPusherEvent) => void): () => void {
		this.aiEventListeners.add(listener);

		return () => {
			this.aiEventListeners.delete(listener);
		};
	}

	/**
	 * Tells the Genie chart cards a metric's verification changed. The framework's verificationChanged event skips
	 * the acting user, so the verification service announces the user's own saves through here.
	 */
	announceMetricVerification(data: MetricVerificationUpdatedEvent): void {
		this.notifyAiEventListeners({ type: PusherEvents.METRIC_VERIFICATION_UPDATED, data });
	}

	// One throwing Genie root must neither starve the other listeners nor break the caller.
	private notifyAiEventListeners(event: AiPusherEvent): void {
		this.aiEventListeners.forEach((listener) => {
			try {
				listener(event);
			} catch (error) {
				this.logError(
					'AI event listener failed',
					{ error, type: event.type },
					Severity.WARNING,
				);
			}
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		socket: SocketService;
	}
}
